import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  ShoppingCartIcon,
  Upload,
  CircleUserRound,
  HandHelping,
  LogOut,
  MenuIcon,
  XIcon,
  LayoutTemplate,
  Briefcase,
  Package,
  Mail,
  Ticket,
  BarChart3,
  Globe,
  Layers,
} from "lucide-react";
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import Badge from './ui/Badge';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
export default function Sidebar() {
  const { user, authToken, logoutUser } = useContext(AuthContext);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const authAxios = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  });

  const fetchSubscriptionStatus = async () => {
    if (!authToken) return;
    try {
      const response = await authAxios.get('/api/get_subscription_status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      devLog.error('Failed to fetch subscription status:', err);
      setSubscriptionStatus({ is_active: false, expiry_date: null });
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again.',
          icon: 'warning',
          confirmButtonColor: "#d45614",
        }).then(() => {
          logoutUser();
        });
      }
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [authToken]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  const sidebarSharedClasses = "fixed inset-y-0 left-0 flex flex-col w-[260px] bg-white border-r border-[var(--color-gray-200)] shadow-[var(--shadow-lg)] transform transition-all duration-300 ease-in-out font-fell";
  const mobileHiddenClasses = "-translate-x-full";
  const mobileVisibleClasses = "translate-x-0";
  const desktopVisibleClasses = "lg:translate-x-0 lg:shadow-[var(--shadow-sm)] lg:border-[var(--color-gray-100)]";

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const toggleButtonBaseClasses = "hidden fixed top-4 p-2 bg-white rounded-[var(--radius-full)] text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] focus:outline-none focus:ring-2 focus:ring-[var(--color-gray-400)] focus:ring-offset-1 transition-all duration-300 ease-in-out shadow-[var(--shadow-md)]";
  const toggleButtonClosedPosition = "left-4";
  const toggleButtonOpenPosition = "left-[200px] top-2 border-transparent shadow-none";

  const handleUploadClick = () => {
    Swal.fire({
        title: 'Activate Your Free Trial',
        text: 'Start your free 30-day trial to upload products and build your store. No credit card needed!',
        icon: 'info',
        confirmButtonText: 'Start Free Trial',
        confirmButtonColor: "#d45614",
        showCancelButton: true,
        cancelButtonText: 'Later',
    }).then((result) => {
        if (result.isConfirmed) {
            navigate('/subscription');
        }
    });
  };

  return (
    <>
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          toggleMobileMenu();
        }}
        className={`${toggleButtonBaseClasses} ${isMobileMenuOpen ? toggleButtonOpenPosition : toggleButtonClosedPosition} touch-manipulation`}
        style={{
          zIndex: 'var(--z-sidebar-toggle)',
          minHeight: '44px',
          minWidth: '44px',
          pointerEvents: 'auto'
        }}
        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        aria-expanded={isMobileMenuOpen}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isMobileMenuOpen ? <XIcon size={24} /> : <MenuIcon size={24} />}
      </motion.button>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black/40 lg:hidden touch-manipulation"
            style={{
              zIndex: 'var(--z-modal-backdrop)',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(false);
            }}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      <motion.aside
        className={`lg:w-[270px] ${sidebarSharedClasses} ${isMobileMenuOpen ? mobileVisibleClasses : mobileHiddenClasses} ${desktopVisibleClasses}`}
        style={{ zIndex: 'var(--z-modal)', borderRadius: 0 }}
        aria-label="Sidebar"
        initial={false}
        animate={isMobileMenuOpen ? "open" : "closed"}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-[var(--color-gray-200)] shrink-0" style={{ borderRadius: 0 }}>
          <Link to="/homepage" className="flex items-center" onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}>
            <img
              src={'https://d3qvi06vol9hrb.cloudfront.net/static/media/logo-img.e08e272c66f1e35ac87c.png'} 
              alt="Logo"
              className="h-8 w-auto object-contain"
              style={{ borderRadius: 0 }}
            />
          </Link>
        </div>

        <nav className="flex-grow p-4 overflow-y-auto custom-scrollbar" style={{ borderRadius: 0 }}>
          {/* ── Dashboard ── */}
          <div className="space-y-1">
            <NavItem
              icon={<HomeIcon size={20} />}
              label="Dashboard"
              to="/homepage"
              currentPath={location.pathname}
              closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
            />
          </div>

          {/* ── Products & Catalog ── */}
          <div className="mt-5">
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gray-400)]">Products</p>
            <div className="space-y-1">
              <NavItem
                icon={<Upload size={20} />}
                label="Upload Product"
                to="/UploadProduct"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
                disabled={!subscriptionStatus.is_active}
                onDisabledClick={handleUploadClick}
                badge={!subscriptionStatus.is_active ? <Badge variant="warning" size="sm">Pro</Badge> : null}
              />
              <NavItem
                icon={<Layers size={20} />}
                label="Collections"
                to="/collections-manager"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<Ticket size={20} />}
                label="Coupons"
                to="/my-coupons"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
            </div>
          </div>

          {/* ── Orders ── */}
          <div className="mt-5">
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gray-400)]">Orders</p>
            <div className="space-y-1">
              <NavItem
                icon={<ShoppingCartIcon size={20} />}
                label="Orders"
                to="/Orders"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<HandHelping size={20} />}
                label="Bid Requests"
                to="/Proposals"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
            </div>
          </div>

          {/* ── Store Design ── */}
          <div className="mt-5">
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gray-400)]">Store</p>
            <div className="space-y-1">
              <NavItem
                icon={<LayoutTemplate size={20} />}
                label="Themes"
                to="/TemplateThemes"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<Globe size={20} />}
                label="SEO"
                to="/seo-settings"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<BarChart3 size={20} />}
                label="Analytics"
                to="/analytics"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
            </div>
          </div>

          {/* ── Account ── */}
          <div className="mt-5">
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-gray-400)]">Account</p>
            <div className="space-y-1">
              <NavItem
                icon={<CircleUserRound size={20} />}
                label="Brand Profile"
                to="/Profile"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<Briefcase size={20} />}
                label="KYC"
                to="/designer-onboarding"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<BarChart3 size={20} />}
                label="Payouts"
                to="/designer-profile"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<Package size={20} />}
                label="Subscription"
                to="/subscription"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
              <NavItem
                icon={<Mail size={20} />}
                label="Support"
                to="/Contact"
                currentPath={location.pathname}
                closeMobileMenu={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
              />
            </div>
          </div>
        </nav>

        <div className="p-4 border-t border-[var(--color-gray-200)] shrink-0">
          <button
            onClick={() => {
              logoutUser();
              if (isMobileMenuOpen) setIsMobileMenuOpen(false);
            }}
            className="flex items-center space-x-3 w-full px-3 py-2.5 rounded-[var(--radius-md)] text-[var(--color-gray-600)] hover:bg-[var(--color-error-light)] hover:text-[var(--color-error)] focus:outline-none focus:ring-2 focus:ring-[var(--color-error-dark)] focus:ring-offset-1 focus:bg-[var(--color-error-light)] focus:text-[var(--color-error)] transition-all duration-200 group"
          >
            <LogOut size={20} className="text-[var(--color-gray-500)] group-hover:text-[var(--color-error)] transition-colors" />
            <span className="text-sm font-medium">Log out</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}

function NavItem({ icon, label, to, currentPath, closeMobileMenu, disabled, onDisabledClick, badge }) {
  const isActive = currentPath === to;

  const handleClick = (e) => {
    if (disabled) {
      e.preventDefault();
      if (onDisabledClick) {
        onDisabledClick();
      }
    } else {
      if (closeMobileMenu) closeMobileMenu();
    }
  };

  return (
    <Link
      to={to}
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
      className={`flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-md)] transition-all duration-200 group focus:outline-none ${
        isActive
          ? 'bg-[var(--color-accent-light)] text-[var(--color-primary)] shadow-[var(--shadow-sm)] focus:ring-2 focus:ring-[var(--color-accent-dark)] focus:ring-offset-1'
          : 'text-[var(--color-gray-700)] hover:bg-[var(--color-gray-100)] hover:text-[var(--color-gray-900)] focus:bg-[var(--color-gray-100)] focus:ring-2 focus:ring-[var(--color-gray-300)] focus:ring-offset-1'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className="flex items-center space-x-3">
        <span className={`transition-colors duration-200 ${
          isActive
            ? 'text-[var(--color-primary)]'
            : 'text-[var(--color-gray-400)] group-hover:text-[var(--color-gray-600)]'
        }`}>
          {icon}
        </span>
        <span className={`text-base font-medium transition-colors duration-200 ${
            isActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-gray-700)] group-hover:text-[var(--color-gray-900)]'
        }`}>
          {label}
        </span>
      </div>
      {badge && <span className="ml-auto">{badge}</span>}
    </Link>
  );
}

